import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Banknote, Plus, Loader2, Eye, Check, Paperclip, Users } from "lucide-react";
import { documentAPI, bankStatementAPI } from "@/lib/api";
import { ManualPaymentDialog } from "@/components/ManualPaymentDialog";
import { AssignPaymentDialog } from "@/components/payments/AssignPaymentDialog";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";
import { usePaymentMethods } from "@/lib/paymentMethods";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

function BankTxStyleClasses(status) {
  if (status === "affecte") return { bg: "bg-emerald-50/50 border-emerald-200", bar: "bg-emerald-500" };
  if (status === "partiellement_affecte") return { bg: "bg-blue-50/50 border-blue-200", bar: "bg-blue-400" };
  return { bg: "bg-amber-50/50 border-amber-200", bar: "bg-amber-400" };
}

export default function PaymentsTab({ clientId, clientName }) {
  const [allDocs, setAllDocs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bankTx, setBankTx] = useState([]);
  const [groupSummary, setGroupSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingUnassigned, setLoadingUnassigned] = useState(true);
  const [assignDialog, setAssignDialog] = useState(null);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [periodType, setPeriodType] = useState("all");
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodQuarter, setPeriodQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const pm = usePaymentMethods();

  const getDateRange = useCallback(() => {
    if (periodType === "year") return { from: `${periodYear}-01-01`, to: `${periodYear}-12-31` };
    if (periodType === "quarter") {
      const m1 = (periodQuarter - 1) * 3 + 1;
      const m2 = periodQuarter * 3;
      const lastDay = new Date(periodYear, m2, 0).getDate();
      return { from: `${periodYear}-${String(m1).padStart(2,"0")}-01`, to: `${periodYear}-${String(m2).padStart(2,"0")}-${lastDay}` };
    }
    if (periodType === "custom" && customFrom && customTo) return { from: customFrom, to: customTo };
    return { from: null, to: null };
  }, [periodType, periodYear, periodQuarter, customFrom, customTo]);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadingUnassigned(true);
    documentAPI.list({ client_id: clientId, type: "facture" })
      .then(r => {
        const docs = r.data?.items || r.data || [];
        setAllDocs(docs);
        setInvoices(docs.filter(d => (d.payments?.length > 0 || d.total_paid > 0)));
        setLoading(false);
      })
      .catch(() => setLoading(false));
    bankStatementAPI.clientUnassigned(clientId)
      .then(r => { setBankTx(r.data || []); setLoadingUnassigned(false); })
      .catch(() => setLoadingUnassigned(false));
  }, [clientId]);

  const reloadGroupSummary = useCallback(() => {
    const { from, to } = getDateRange();
    bankStatementAPI.clientGroupSummary(clientId, from, to)
      .then(r => setGroupSummary(r.data))
      .catch(() => {});
  }, [clientId, getDateRange]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadGroupSummary(); }, [reloadGroupSummary]);

  const openAssignDialog = async (tx) => {
    const txAvailable = tx.remaining_amount ?? tx.amount;
    setAssignDialog({ tx, txAvailable, unpaidInvoices: [], loading: true, selectedInvoice: null });
    try {
      const r = await bankStatementAPI.clientRelatedUnpaid(clientId);
      const unpaid = r.data || [];
      unpaid.sort((a, b) => {
        const diffA = Math.abs((a.remaining || a.total_ttc || 0) - txAvailable);
        const diffB = Math.abs((b.remaining || b.total_ttc || 0) - txAvailable);
        const matchA = diffA < 1;
        const matchB = diffB < 1;
        if (matchA && !matchB) return -1;
        if (matchB && !matchA) return 1;
        if (a.is_current_client !== b.is_current_client) return a.is_current_client ? -1 : 1;
        return (a.date || "").localeCompare(b.date || "");
      });
      const bestMatch = unpaid.length > 0 && Math.abs((unpaid[0].remaining || unpaid[0].total_ttc || 0) - txAvailable) < 1 ? unpaid[0].id : null;
      const bestInv = bestMatch ? unpaid[0] : null;
      const defaultAmount = bestInv ? Math.min(txAvailable, bestInv.remaining || bestInv.total_ttc || 0) : txAvailable;
      setAssignDialog(prev => ({ ...prev, unpaidInvoices: unpaid, loading: false, selectedInvoice: bestMatch, assignAmount: defaultAmount }));
    } catch {
      setAssignDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const onSelectInvoice = (inv) => {
    const remaining = inv.remaining ?? (inv.total_ttc - (inv.total_paid || 0));
    const txAvailable = assignDialog?.txAvailable || assignDialog?.tx?.amount || 0;
    setAssignDialog(prev => ({ ...prev, selectedInvoice: inv.id, assignAmount: Math.min(txAvailable, remaining) }));
  };

  const confirmAssign = async () => {
    if (!assignDialog?.selectedInvoice || !assignDialog?.tx) return;
    const tx = assignDialog.tx;
    const amount = parseFloat(assignDialog.assignAmount) || 0;
    if (amount <= 0) { toast.error("Montant invalide"); return; }
    let payDate = tx.date || "";
    const parts = payDate.split("/");
    if (parts.length === 3) payDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
    try {
      await documentAPI.addPayment(assignDialog.selectedInvoice, {
        date: payDate, mode: pm.bankTransferMode, amount_ttc: amount,
        comment: `Rapprochement bancaire - ${tx.label?.substring(0, 60) || ""}`,
        bank_tx_ref: tx.bank_tx_ref || "",
      });
      toast.success(`${formatEur(amount)} affecte avec succes`);
      setAssignDialog(null);
      reload();
      reloadGroupSummary();
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const { allPayments, totalPaid, totalInvoiced } = useMemo(() => {
    const payments = [];
    let paid = 0;
    let invoiced = 0;
    for (const doc of allDocs) invoiced += doc.total_ttc || 0;
    for (const inv of invoices) {
      for (const p of (inv.payments || [])) {
        paid += p.amount_ttc || 0;
        payments.push({ ...p, invoice_number: inv.number, invoice_id: inv.id, invoice_ttc: inv.total_ttc });
      }
    }
    payments.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    return { allPayments: payments, totalPaid: paid, totalInvoiced: invoiced };
  }, [allDocs, invoices]);

  const unassignedCount = useMemo(() => bankTx.filter(t => t.status === "non_affecte" || t.status === "partiellement_affecte").length, [bankTx]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin h-5 w-5 text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-semibold text-slate-800">Règlements</h2>
        <Button className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold tracking-wider" onClick={() => setShowManualPayment(true)} data-testid="new-manual-payment-btn">
          <Plus size={14} className="mr-1.5" /><span className="hidden sm:inline">Nouveau règlement</span><span className="sm:hidden">Nouveau</span>
        </Button>
      </div>

      <ManualPaymentDialog open={showManualPayment} onOpenChange={setShowManualPayment} clientId={clientId} clientName={clientName} onSuccess={() => { reload(); reloadGroupSummary(); }} />

      {/* Bank transactions */}
      {loadingUnassigned ? (
        <div className="flex justify-center py-4"><Loader2 className="animate-spin h-4 w-4 text-amber-500" /></div>
      ) : bankTx.length > 0 && (
        <div data-testid="bank-transactions-section">
          <div className="flex items-center gap-2 mb-3">
            <Banknote size={16} className="text-slate-600" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Transactions bancaires</h3>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-500">{bankTx.length}</Badge>
            {unassignedCount > 0 && <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">{unassignedCount} a affecter</Badge>}
          </div>
          <div className="space-y-1.5">
            {bankTx.map((u, i) => {
              const isAssigned = u.status === "affecte";
              const isPartial = u.status === "partiellement_affecte";
              const isUnassigned = u.status === "non_affecte";
              const txStyle = BankTxStyleClasses(u.status);
              return (
                <div key={`tx-${u.date}-${u.amount}-${i}`} className={`flex items-center gap-2 rounded-md border px-2.5 py-2 ${txStyle.bg}`} data-testid={`bank-tx-${i}`}>
                  <div className={`w-1 h-8 rounded-full shrink-0 ${txStyle.bar}`} />
                  <div className="text-[11px] text-slate-500 shrink-0 w-[68px]">{u.date || "-"}</div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs text-slate-700 truncate" title={u.label}>{u.label || "-"}</p>
                    {(isAssigned || isPartial) && u.assignments && u.assignments.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        {u.assignments.map((a, ai) => (
                          <div key={a.invoice_id || `assign-${ai}`} className="flex items-center gap-1">
                            <Check size={10} className="text-emerald-500 shrink-0" />
                            <button className="font-mono text-[11px] font-medium text-indigo-600 hover:underline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${a.invoice_id}/preview`, '_blank')}>{a.invoice_number}</button>
                            <span className="text-[10px] text-slate-400">{formatEur(a.amount)}</span>
                            {a.is_other_company && <Badge className="bg-indigo-100 text-indigo-700 text-[8px] px-1 py-0">{a.company}</Badge>}
                          </div>
                        ))}
                      </div>
                    )}
                    {isAssigned && (!u.assignments || u.assignments.length === 0) && u.assigned_invoice_number && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Check size={10} className="text-emerald-500 shrink-0" />
                        <button className="font-mono text-[11px] font-medium text-indigo-600 hover:underline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${u.assigned_invoice_id}/preview`, '_blank')} data-testid={`bank-tx-invoice-link-${i}`}>{u.assigned_invoice_number}</button>
                        {u.is_other_company && <Badge variant="outline" className="text-[8px] px-1 py-0 text-slate-500 border-slate-300">{u.assigned_company}</Badge>}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {isPartial ? (
                      <div>
                        <p className="text-[10px] text-slate-400 line-through">{formatEur(u.original_amount)}</p>
                        <p className="text-xs font-bold text-blue-700">reste {formatEur(u.remaining_amount)}</p>
                      </div>
                    ) : (
                      <div className={`text-xs font-bold ${isAssigned ? "text-emerald-600" : "text-amber-700"}`}>{formatEur(u.amount)}</div>
                    )}
                  </div>
                  {(isUnassigned || isPartial) && (
                    <Button size="sm" variant="outline" className={`h-6 text-[10px] px-2 shrink-0 ${isPartial ? "border-blue-400 text-blue-700 hover:bg-blue-100" : "border-amber-400 text-amber-700 hover:bg-amber-100"}`} onClick={() => openAssignDialog(u)} data-testid={`assign-btn-${i}`}>
                      Affecter{isPartial ? ` ${formatEur(u.remaining_amount)}` : ""}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Group summary */}
      {groupSummary && groupSummary.companies.length > 1 && (
        <div className="rounded-lg border border-indigo-200 bg-white p-3" data-testid="group-summary-section">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-indigo-600" />
            <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wider">Point global - {groupSummary.contact_name}</h3>
            <Badge variant="outline" className="text-[9px] px-1 py-0 text-indigo-500 border-indigo-300">{groupSummary.companies.length} sociétés</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="period-selector">
            <button onClick={() => setPeriodType("all")} className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${periodType === "all" ? "bg-indigo-600 text-white" : "bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-50"}`} data-testid="period-all">Tout</button>
            {[2024, 2025, 2026].map(y => (
              <button key={y} onClick={() => { setPeriodType("year"); setPeriodYear(y); }} className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${periodType === "year" && periodYear === y ? "bg-indigo-600 text-white" : "bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-50"}`} data-testid={`period-${y}`}>{y}</button>
            ))}
            <span className="text-slate-300 mx-0.5">|</span>
            {[1,2,3,4].map(q => (
              <button key={q} onClick={() => { setPeriodType("quarter"); setPeriodYear(new Date().getFullYear()); setPeriodQuarter(q); }} className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${periodType === "quarter" && periodQuarter === q && periodYear === new Date().getFullYear() ? "bg-indigo-600 text-white" : "bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-50"}`} data-testid={`period-q${q}`}>T{q}</button>
            ))}
            <span className="text-slate-300 mx-0.5">|</span>
            <DateField value={customFrom} onChange={e => { setCustomFrom(e.target.value); if (customTo) setPeriodType("custom"); }} className="h-5 px-1 text-[10px] border border-indigo-300 rounded bg-white text-slate-700 w-[100px]" data-testid="period-from" />
            <span className="text-[10px] text-slate-400">a</span>
            <DateField value={customTo} onChange={e => { setCustomTo(e.target.value); if (customFrom) setPeriodType("custom"); }} className="h-5 px-1 text-[10px] border border-indigo-300 rounded bg-white text-slate-700 w-[100px]" data-testid="period-to" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs bg-white">
              <thead>
                <tr className="border-b border-indigo-200 bg-indigo-50">
                  <th className="text-left py-1 pr-2 text-indigo-600 font-semibold">Société</th>
                  <th className="text-right py-1 px-1 text-indigo-600 font-semibold">Facture</th>
                  <th className="text-right py-1 px-1 text-indigo-600 font-semibold">Réglé</th>
                  <th className="text-right py-1 px-1 text-indigo-600 font-semibold">Solde</th>
                  <th className="text-right py-1 pl-1 text-indigo-600 font-semibold">Impayées</th>
                </tr>
              </thead>
              <tbody>
                {groupSummary.companies.map(c => (
                  <tr key={c.client_id} className={`border-b border-indigo-100 ${c.is_current ? "font-medium bg-indigo-50/50" : "bg-white"}`} data-testid={`group-row-${c.client_id}`}>
                    <td className="py-1.5 pr-2">
                      {c.is_current ? <span className="text-indigo-900">{c.company}</span> : <a href={`/clients/${c.client_id}`} className="text-indigo-600 hover:underline">{c.company}</a>}
                    </td>
                    <td className="text-right py-1.5 px-1 text-slate-700">{formatEur(c.total_invoiced)}</td>
                    <td className="text-right py-1.5 px-1 text-emerald-600">{formatEur(c.total_paid)}</td>
                    <td className={`text-right py-1.5 px-1 font-medium ${c.balance > 0.01 ? "text-red-600" : "text-emerald-600"}`}>{formatEur(c.balance)}</td>
                    <td className="text-right py-1.5 pl-1">{c.nb_unpaid > 0 ? <Badge className="bg-red-100 text-red-700 text-[9px] px-1.5 py-0">{c.nb_unpaid}</Badge> : <span className="text-emerald-500">0</span>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-indigo-300 font-bold bg-indigo-50">
                  <td className="py-1.5 pr-2 text-indigo-900">TOTAL</td>
                  <td className="text-right py-1.5 px-1 text-slate-900">{formatEur(groupSummary.total_invoiced)}</td>
                  <td className="text-right py-1.5 px-1 text-emerald-700">{formatEur(groupSummary.total_paid)}</td>
                  <td className={`text-right py-1.5 px-1 ${groupSummary.total_balance > 0.01 ? "text-red-700" : "text-emerald-700"}`}>{formatEur(groupSummary.total_balance)}</td>
                  <td className="text-right py-1.5 pl-1"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total facture</p>
          <p className="text-base font-bold text-slate-900" data-testid="payments-total-invoiced">{formatEur(totalInvoiced)}</p>
        </div>
        <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-[10px] text-emerald-500 uppercase tracking-wider">Total réglé</p>
          <p className="text-base font-bold text-emerald-700" data-testid="payments-total-paid">{formatEur(totalPaid)}</p>
        </div>
        <div className={`p-2.5 rounded-lg border ${totalInvoiced - totalPaid > 0.01 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
          <p className={`text-[10px] uppercase tracking-wider ${totalInvoiced - totalPaid > 0.01 ? "text-red-400" : "text-emerald-500"}`}>Solde</p>
          <p className={`text-base font-bold ${totalInvoiced - totalPaid > 0.01 ? "text-red-600" : "text-emerald-700"}`} data-testid="payments-balance">{formatEur(totalInvoiced - totalPaid)}</p>
        </div>
      </div>

      {/* Payments table */}
      {allPayments.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">Aucun règlement enregistré</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="uppercase text-[10px] text-slate-500">Date</TableHead>
                <TableHead className="uppercase text-[10px] text-slate-500">Facture</TableHead>
                <TableHead className="uppercase text-[10px] text-slate-500">Mode</TableHead>
                <TableHead className="uppercase text-[10px] text-slate-500 text-right">Montant</TableHead>
                <TableHead className="uppercase text-[10px] text-slate-500 hidden lg:table-cell">Commentaire</TableHead>
                <TableHead className="uppercase text-[10px] text-slate-500 w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allPayments.map((p, i) => (
                <TableRow key={p.id || i} data-testid={`payment-line-${i}`}>
                  <TableCell className="text-xs py-1.5">{p.date ? new Date(p.date + "T00:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                  <TableCell className="py-1.5">
                    <button className="font-mono text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${p.invoice_id}/preview`, '_blank')} data-testid={`view-invoice-${p.invoice_number}`}>{p.invoice_number}</button>
                  </TableCell>
                  <TableCell className="text-xs py-1.5" data-testid={`payment-line-mode-${i}`}>{pm.label(p.mode) || "-"}</TableCell>
                  <TableCell className="text-xs font-medium text-right text-emerald-600 py-1.5">{formatEur(p.amount_ttc)}</TableCell>
                  <TableCell className="text-xs text-slate-500 py-1.5 hidden lg:table-cell">{p.comment || "-"}</TableCell>
                  <TableCell className="py-1.5">
                    <div className="flex items-center gap-1">
                      {p.cheque_mp_id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Voir le chèque scanné"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/manual-payments/${p.cheque_mp_id}/cheque-image`, '_blank')}
                          data-testid={`view-cheque-${i}`}
                        >
                          <Paperclip size={12} className="text-indigo-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-6 w-6" title="Voir la facture" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${p.invoice_id}/preview`, '_blank')} data-testid={`preview-payment-invoice-${i}`}>
                        <Eye size={12} className="text-slate-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={3} className="text-xs">Total ({allPayments.length})</TableCell>
                <TableCell className="text-xs text-right text-emerald-700">{formatEur(totalPaid)}</TableCell>
                <TableCell colSpan={2} className="hidden lg:table-cell" />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Assign Dialog */}
      <AssignPaymentDialog assignDialog={assignDialog} setAssignDialog={setAssignDialog} onSelectInvoice={onSelectInvoice} confirmAssign={confirmAssign} />
    </div>
  );
}
